import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, AlertTriangle, Lightbulb, Loader2, X, LayoutList, Columns, Download, Mail, AlertCircle, Inbox, SearchX, UserPlus } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'react-toastify';
import { DndContext, DragOverlay, PointerSensor, useSensor, useSensors, useDroppable, useDraggable } from '@dnd-kit/core';

import {
  Card, Button, Badge, Input, Select, AreaChip, AreaFilter,
  TypePill, TypePillGroup, ToggleGroup, ToggleGroupItem, KpiCardSplit, KanbanCard,
  Textarea, Dialog, DialogHeader, DialogTitle, DialogContent, DialogFooter, Skeleton,
} from '../ui-v2';
import { cn } from '../../../lib/cn';
import { getIcon } from '../inventario/icons';
import { listPanelTickets, getPanelKpis, getPanelEquipo, exportarPanelTickets, patchPanelTicket } from './api';

// MANTENER SYNCED con backend services/flujosEstado.js
const FLUJOS = {
  incidencia: ['pendiente', 'en_revision', 'en_desarrollo', 'resuelto'],
  sugerencia: ['recibida', 'en_estudio', 'aceptada', 'implementada'],
};
const LATERALES = { incidencia: ['rechazado', 'descartado'], sugerencia: ['rechazada'] };
const ESTADO_META = {
  pendiente: { label: 'Pendiente', variant: 'warning' }, en_revision: { label: 'En revisión', variant: 'default' },
  en_desarrollo: { label: 'En desarrollo', variant: 'default' }, resuelto: { label: 'Resuelto', variant: 'success' },
  rechazado: { label: 'Rechazado', variant: 'destructive' }, descartado: { label: 'Descartado', variant: 'secondary' },
  recibida: { label: 'Recibida', variant: 'secondary' }, en_estudio: { label: 'En estudio', variant: 'default' },
  aceptada: { label: 'Aceptada', variant: 'success' }, implementada: { label: 'Implementada', variant: 'success' },
  rechazada: { label: 'Rechazada', variant: 'destructive' },
};
const PRIORIDADES = ['baja', 'media', 'alta', 'critica'];
const PRIORIDAD_LABEL = { baja: 'Baja', media: 'Media', alta: 'Alta', critica: 'Crítica' };
const PRIORIDAD_VARIANT = { baja: 'secondary', media: 'warning', alta: 'destructive', critica: 'destructive' };
// Estados cuyo destino dispara email (espejo de backend services/flujosEstado.js).
// En el kanban son además los únicos "importantes": exigen modal de confirmación.
const ESTADOS_EMAIL = ['en_desarrollo', 'aceptada', 'resuelto', 'implementada'];

function hace(f) { try { return formatDistanceToNow(new Date(f), { addSuffix: true, locale: es }); } catch { return ''; } }
function agingDe(createdAt) {
  const d = (Date.now() - new Date(createdAt).getTime()) / 86400000;
  if (d >= 7) return 'danger'; if (d >= 3) return 'warning'; return 'none';
}

export default function SoportePanel() {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();

  // ---- filtros desde la URL ----
  const tipo = sp.get('tipo');
  const categoria = sp.get('categoria');       // id string | null
  const estado = sp.get('estado');
  const origen = sp.get('origen');
  const prioridad = sp.get('prioridad');
  const asignado = sp.get('asignado');
  const q = sp.get('q') || '';
  const page = parseInt(sp.get('page'), 10) || 1;
  const pageSize = parseInt(sp.get('pageSize'), 10) || 25;
  const vista = sp.get('vista') === 'kanban' ? 'kanban' : 'tabla';

  const [kpis, setKpis] = useState(null);
  const [equipo, setEquipo] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [qInput, setQInput] = useState(q);
  const [intentos, setIntentos] = useState(0); // re-dispara los fetch al reintentar
  const debRef = useRef(null);

  const reintentar = useCallback(() => { setError(null); setIntentos((n) => n + 1); }, []);

  // actualizar searchParams (value null/'' → borra). Resetea page salvo que se pase page.
  const update = useCallback((patch) => {
    setSp((prev) => {
      const next = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(patch)) {
        if (v === null || v === undefined || v === '') next.delete(k);
        else next.set(k, String(v));
      }
      if (!('page' in patch)) next.set('page', '1');
      return next;
    });
  }, [setSp]);

  // KPIs + equipo: una vez al montar.
  useEffect(() => {
    let vivo = true;
    Promise.all([getPanelKpis(), getPanelEquipo()])
      .then(([k, e]) => { if (vivo) { setKpis(k); setEquipo(e.equipo || []); } })
      .catch(() => { if (vivo) setError('No se pudieron cargar los datos del panel.'); });
    return () => { vivo = false; };
  }, [intentos]);

  // tickets: cuando cambian los filtros (sp).
  useEffect(() => {
    let vivo = true;
    setCargando(true); setError(null);
    const params = {};
    if (tipo) params.tipo = tipo;
    if (categoria) params.categoria = categoria;
    if (estado) params.estado = estado;
    if (origen) params.origen = origen;
    if (prioridad && tipo !== 'sugerencia') params.prioridad = prioridad;
    if (asignado) params.asignado = asignado;
    if (q.trim().length >= 2) params.q = q.trim();
    params.page = vista === 'kanban' ? 1 : page;
    params.pageSize = vista === 'kanban' ? 100 : pageSize;

    listPanelTickets(params)
      .then((d) => { if (vivo) { setTickets(d.tickets); setPagination(d.pagination); } })
      .catch((e) => {
        if (!vivo) return;
        // 400 = filtros inválidos (transitorio) → toast, no error state.
        if (e?.response?.status === 400) toast.warn(e?.response?.data?.message || 'Filtros no válidos.');
        else setError('No se pudieron cargar los tickets.');
      })
      .finally(() => { if (vivo) setCargando(false); });
    return () => { vivo = false; };
  }, [tipo, categoria, estado, origen, prioridad, asignado, q, page, pageSize, vista, intentos]);

  // debounce de búsqueda
  function onBuscar(v) {
    setQInput(v);
    clearTimeout(debRef.current);
    debRef.current = setTimeout(() => update({ q: v }), 300);
  }

  const hayFiltros = !!(tipo || categoria || estado || origen || prioridad || asignado || q);
  function limpiar() { setQInput(''); setSp(vista === 'kanban' ? { vista: 'kanban' } : {}); }

  // Exportar a Excel: exporta lo que se ve (mismos filtros que la tabla, sin paginar).
  const [exportando, setExportando] = useState(false);
  async function exportar() {
    if (exportando) return;
    setExportando(true);
    try {
      const params = {};
      if (tipo) params.tipo = tipo;
      if (categoria) params.categoria = categoria;
      if (estado) params.estado = estado;
      if (origen) params.origen = origen;
      if (prioridad && tipo !== 'sugerencia') params.prioridad = prioridad;
      if (asignado) params.asignado = asignado;
      if (q.trim().length >= 2) params.q = q.trim();

      const resp = await exportarPanelTickets(params);

      // Nombre desde Content-Disposition si viene; si no, fallback.
      const cd = resp.headers['content-disposition'] || '';
      const m = /filename="?([^"]+)"?/.exec(cd);
      const nombre = m ? m[1] : 'soporte-tickets.xlsx';

      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = nombre;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      // El cuerpo de error llega como Blob (responseType: 'blob'): lo parseamos.
      let codigo = null;
      try {
        const txt = await e?.response?.data?.text?.();
        if (txt) codigo = JSON.parse(txt).error;
      } catch { /* noop */ }
      if (codigo === 'demasiados_resultados') {
        toast.warn('Más de 5000 tickets — refina los filtros antes de exportar.');
      } else {
        toast.error('No se pudo exportar.');
      }
    } finally {
      setExportando(false);
    }
  }

  // estados disponibles para el select (según tipo; si no hay tipo, unión)
  const estadosSelect = tipo
    ? [...FLUJOS[tipo], ...LATERALES[tipo]]
    : [...new Set([...FLUJOS.incidencia, ...LATERALES.incidencia, ...FLUJOS.sugerencia, ...LATERALES.sugerencia])];

  return (
    <div className="px-6 py-6 min-h-full">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Panel de soporte</h1>
          <p className="text-sm text-slate-500 mt-1">Gestiona incidencias y sugerencias del equipo.</p>
        </div>
        <Button variant="outline" onClick={exportar} disabled={exportando} className="shrink-0">
          {exportando
            ? <><Loader2 className="w-4 h-4 animate-spin" /> Exportando…</>
            : <><Download className="w-4 h-4" /> Exportar</>}
        </Button>
      </div>

      {/* KPIs */}
      {kpis ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <KpiCardSplit label="Incidencias pendientes" value={String(kpis.incidencias.pendientes)} topBorderClass="border-t-primary"
            splitA={{ label: 'Internas', value: String(kpis.incidencias.interno) }} splitB={{ label: 'Colab.', value: String(kpis.incidencias.colaborador) }} />
          <KpiCardSplit label="Incidencias en curso" value={String(kpis.incidencias.en_curso)} topBorderClass="border-t-amber-500"
            splitA={{ label: 'Internas', value: String(kpis.incidencias.interno) }} splitB={{ label: 'Colab.', value: String(kpis.incidencias.colaborador) }} />
          <KpiCardSplit label="Sugerencias en estudio" value={String(kpis.sugerencias.en_estudio)} topBorderClass="border-t-purple-500"
            splitA={{ label: 'Internas', value: String(kpis.sugerencias.interno) }} splitB={{ label: 'Colab.', value: String(kpis.sugerencias.colaborador) }} />
          <KpiCardSplit label="Resueltas este mes" value={String(kpis.incidencias.resueltos_mes)} topBorderClass="border-t-emerald-500" />
        </div>
      ) : !error && <SkeletonKpis />}

      {/* Filtro de áreas */}
      {kpis ? (
        <div className="flex flex-wrap gap-2 mb-4">
          <AreaFilter active={!categoria} onClick={() => update({ categoria: null })}>Todas</AreaFilter>
          {kpis.por_categoria.map((c) => (
            <AreaFilter key={c.categoria_id} active={categoria === String(c.categoria_id)} count={c.abiertos}
              onClick={() => update({ categoria: c.categoria_id })}>
              {c.nombre}
            </AreaFilter>
          ))}
        </div>
      ) : !error && <SkeletonAreaFilter />}

      {/* Filtros secundarios + toggle vista */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <TypePillGroup>
          <TypePill active={tipo === 'incidencia'} icon={<AlertTriangle size={16} />} label="Incidencias"
            onClick={() => update({ tipo: tipo === 'incidencia' ? null : 'incidencia', estado: null, prioridad: null })} />
          <TypePill active={tipo === 'sugerencia'} icon={<Lightbulb size={16} />} label="Sugerencias"
            onClick={() => update({ tipo: tipo === 'sugerencia' ? null : 'sugerencia', estado: null, prioridad: null })} />
        </TypePillGroup>

        <Select value={estado || ''} onChange={(e) => update({ estado: e.target.value || null })} className="w-auto">
          <option value="">Todos los estados</option>
          {estadosSelect.map((s) => <option key={s} value={s}>{ESTADO_META[s]?.label || s}</option>)}
        </Select>

        <Select value={origen || ''} onChange={(e) => update({ origen: e.target.value || null })} className="w-auto">
          <option value="">Todos los orígenes</option>
          <option value="interno">Interno</option>
          <option value="colaborador">Colaborador</option>
        </Select>

        {tipo === 'incidencia' && (
          <Select value={prioridad || ''} onChange={(e) => update({ prioridad: e.target.value || null })} className="w-auto">
            <option value="">Todas las prioridades</option>
            {PRIORIDADES.map((p) => <option key={p} value={p}>{PRIORIDAD_LABEL[p]}</option>)}
          </Select>
        )}

        <Select value={asignado || ''} onChange={(e) => update({ asignado: e.target.value || null })} className="w-auto">
          <option value="">Todos los asignados</option>
          <option value="sin_asignar">Sin asignar</option>
          {equipo.map((t) => <option key={t.id} value={t.id}>{t.nombre}</option>)}
        </Select>

        <Input
          icon={<Search />}
          wrapperClassName="w-56"
          value={qInput}
          onChange={(e) => onBuscar(e.target.value)}
          placeholder="Buscar nº o título…"
        />

        {hayFiltros && (
          <Button variant="ghost" size="sm" onClick={limpiar}><X className="w-3.5 h-3.5" />Limpiar filtros</Button>
        )}

        <div className="ml-auto">
          <ToggleGroup>
            <ToggleGroupItem active={vista === 'tabla'} onClick={() => update({ vista: null, page: 1 })}>
              <LayoutList className="w-3.5 h-3.5" />Tabla
            </ToggleGroupItem>
            <ToggleGroupItem active={vista === 'kanban'} onClick={() => update({ vista: 'kanban', page: 1 })}>
              <Columns className="w-3.5 h-3.5" />Kanban
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
      </div>

      {/* Contenido */}
      {error ? (
        <ErrorPanel mensaje={error} onRetry={reintentar} />
      ) : cargando ? (
        vista === 'kanban' ? <SkeletonKanban /> : <SkeletonTabla />
      ) : tickets.length === 0 ? (
        <EmptyTickets hayFiltros={hayFiltros} onLimpiar={limpiar} />
      ) : vista === 'kanban' ? (
        <KanbanView tickets={tickets} tipo={tipo} total={pagination?.total} navigate={navigate} setTickets={setTickets} />
      ) : (
        <TablaView tickets={tickets} pagination={pagination} pageSize={pageSize} update={update} navigate={navigate} />
      )}
    </div>
  );
}

// ───────── Estados de carga / vacío / error ─────────
function SkeletonKpis() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="rounded-lg border border-slate-200 bg-white p-4">
          <Skeleton className="h-3 w-28 mb-3" />
          <Skeleton className="h-7 w-16 mb-3" />
          <div className="flex gap-4"><Skeleton className="h-3 w-12" /><Skeleton className="h-3 w-12" /></div>
        </div>
      ))}
    </div>
  );
}

function SkeletonAreaFilter() {
  return (
    <div className="flex flex-wrap gap-2 mb-4">
      {[44, 68, 56, 76, 50].map((w, i) => <Skeleton key={i} className="h-7" width={w} />)}
    </div>
  );
}

function SkeletonTabla() {
  return (
    <Card className="p-0 overflow-hidden border-slate-200">
      <div className="px-3 py-2.5 border-b border-slate-100"><Skeleton className="h-3 w-40" /></div>
      <div className="divide-y divide-slate-50">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="flex items-center gap-4 px-3 py-3">
            <Skeleton className="h-3 w-20" /><Skeleton className="h-3 w-12" />
            <Skeleton className="h-3 flex-1" /><Skeleton className="h-3 w-16" /><Skeleton className="h-3 w-16" />
          </div>
        ))}
      </div>
    </Card>
  );
}

function SkeletonKanban() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-start">
      {[0, 1, 2, 3].map((c) => (
        <div key={c} className="bg-slate-50 rounded-lg p-2.5">
          <div className="flex items-center justify-between px-1 pb-2 mb-1"><Skeleton className="h-3 w-20" /><Skeleton className="h-3 w-4" /></div>
          <div className="space-y-2">
            {[0, 1].map((k) => (
              <div key={k} className="bg-card border border-border rounded-lg p-3">
                <Skeleton className="h-2.5 w-16 mb-2" /><Skeleton className="h-3 w-full mb-1" />
                <Skeleton className="h-3 w-2/3 mb-2" /><Skeleton className="h-2.5 w-24" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyTickets({ hayFiltros, onLimpiar }) {
  const Icono = hayFiltros ? SearchX : Inbox;
  return (
    <div className="py-16 text-center">
      <Icono className="w-10 h-10 mx-auto text-slate-300 mb-3" />
      <p className="text-sm font-medium text-slate-700">No hay tickets que coincidan</p>
      <p className="text-sm text-slate-500 mt-1">Prueba a quitar algún filtro o ampliar el rango.</p>
      {hayFiltros && (
        <Button variant="outline" size="sm" className="mt-4" onClick={onLimpiar}><X className="w-3.5 h-3.5" />Limpiar filtros</Button>
      )}
    </div>
  );
}

function ErrorPanel({ mensaje, onRetry }) {
  return (
    <div className="py-16 text-center">
      <AlertCircle className="w-10 h-10 mx-auto text-red-400 mb-3" />
      <p className="text-sm font-medium text-slate-700">{mensaje || 'No se pudieron cargar los datos del panel'}</p>
      <Button variant="outline" size="sm" className="mt-4" onClick={onRetry}>Reintentar</Button>
    </div>
  );
}

function OrigenBadge({ origen }) {
  return <Badge variant={origen === 'colaborador' ? 'warning' : 'secondary'} className="text-[10px]">
    {origen === 'colaborador' ? 'Colab.' : 'Interno'}
  </Badge>;
}

function TablaView({ tickets, pagination, pageSize, update, navigate }) {
  return (
    <Card className="p-0 overflow-hidden border-slate-200">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100 text-left text-[11px] uppercase tracking-wider text-slate-500">
              <th className="px-3 py-2.5 font-medium">Número</th><th className="px-3 py-2.5 font-medium">Tipo</th>
              <th className="px-3 py-2.5 font-medium">Título</th><th className="px-3 py-2.5 font-medium">Área</th>
              <th className="px-3 py-2.5 font-medium">Prioridad</th><th className="px-3 py-2.5 font-medium">Estado</th>
              <th className="px-3 py-2.5 font-medium">Asignado</th><th className="px-3 py-2.5 font-medium">Autor</th>
              <th className="px-3 py-2.5 font-medium">Creado</th>
            </tr>
          </thead>
          <tbody>
            {tickets.map((t) => {
              const est = ESTADO_META[t.estado] || { label: t.estado, variant: 'secondary' };
              return (
                <tr key={t.id} onClick={() => navigate(`/soporte/panel/ticket/${t.id}`)}
                  className="border-b border-slate-50 last:border-0 cursor-pointer hover:bg-slate-50">
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1.5">
                      <code className="text-[11px] font-mono text-slate-500">{t.numero}</code>
                      {t.creado_por != null && (
                        <span title="Creado por soporte" className="inline-flex text-slate-400 shrink-0">
                          <UserPlus className="w-3.5 h-3.5" />
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2.5"><Badge variant="outline" className="text-[10px]">{t.tipo === 'incidencia' ? 'Inc.' : 'Sug.'}</Badge></td>
                  <td className="px-3 py-2.5 max-w-xs truncate text-slate-900">{t.titulo}</td>
                  <td className="px-3 py-2.5">{t.categoria && <AreaChip area={t.categoria.color || 'otros'} icon={t.categoria.icono ? React.createElement(getIcon(t.categoria.icono), { className: 'w-3 h-3' }) : null}>{t.categoria.nombre}</AreaChip>}</td>
                  <td className="px-3 py-2.5">{t.prioridad
                    ? <Badge variant={PRIORIDAD_VARIANT[t.prioridad]}>{PRIORIDAD_LABEL[t.prioridad]}</Badge>
                    : t.tipo === 'incidencia'
                      ? <Badge variant="secondary">Sin prioridad</Badge>
                      : <span className="text-slate-300">—</span>}</td>
                  <td className="px-3 py-2.5"><Badge variant={est.variant}>{est.label}</Badge></td>
                  <td className="px-3 py-2.5 text-slate-700">{t.asignado ? t.asignado.nombre : <span className="text-slate-400">Sin asignar</span>}</td>
                  <td className="px-3 py-2.5"><div className="flex items-center gap-1.5"><span className="text-slate-700 truncate max-w-[120px]">{t.autor?.nombre}</span><OrigenBadge origen={t.origen} /></div></td>
                  <td className="px-3 py-2.5 text-[11px] text-slate-400 whitespace-nowrap">{hace(t.createdAt)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {pagination && (
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-slate-100 text-sm">
          <span className="text-slate-500">Página {pagination.page} de {pagination.totalPages || 1} · {pagination.total} tickets</span>
          <div className="flex items-center gap-2">
            <Select value={String(pageSize)} onChange={(e) => update({ pageSize: e.target.value })} className="w-auto">
              {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}/pág.</option>)}
            </Select>
            <Button variant="outline" size="sm" disabled={pagination.page <= 1} onClick={() => update({ page: pagination.page - 1 })}>Anterior</Button>
            <Button variant="outline" size="sm" disabled={pagination.page >= pagination.totalPages} onClick={() => update({ page: pagination.page + 1 })}>Siguiente</Button>
          </div>
        </div>
      )}
    </Card>
  );
}

// Columna del kanban como zona soltable. `dim`: atenuada (destino no válido).
function ColumnaDroppable({ col, dim, count, children }) {
  const { setNodeRef, isOver } = useDroppable({ id: col });
  return (
    <div
      ref={setNodeRef}
      className={cn(
        'bg-slate-50 rounded-lg p-2.5 transition-opacity',
        dim && 'opacity-40',
        isOver && !dim && 'ring-2 ring-primary/40',
      )}
    >
      <div className="flex items-center justify-between px-1 pb-2 mb-1">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">{ESTADO_META[col]?.label || col}</span>
        <span className="text-[10px] text-slate-400">{count}</span>
      </div>
      <div className="space-y-2 min-h-[12px]">{children}</div>
    </div>
  );
}

// Card arrastrable. El click navega al detalle salvo que se acabe de arrastrar
// (draggedRef evita el click sintético que dispara el navegador tras soltar).
function CardArrastrable({ t, navigate, draggedRef }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: String(t.id) });
  return (
    <div ref={setNodeRef} style={{ touchAction: 'none', opacity: isDragging ? 0.4 : 1 }} {...attributes} {...listeners}>
      <KanbanCard priority={t.prioridad} aging={agingDe(t.createdAt)}
        id={t.numero} title={t.titulo} meta={`${t.autor?.nombre || '—'} · ${hace(t.createdAt)}`}
        onClick={() => { if (draggedRef.current) return; navigate(`/soporte/panel/ticket/${t.id}`); }} />
    </div>
  );
}

function KanbanView({ tickets, tipo, total, navigate, setTickets }) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
  const [activeId, setActiveId] = useState(null);
  const [modal, setModal] = useState(null); // { ticket, destino } | null
  const [nota, setNota] = useState('');
  const [guardando, setGuardando] = useState(false);
  const draggedRef = useRef(false);

  if (!tipo) {
    return <div className="py-16 text-center text-sm text-slate-500">Selecciona <strong>Incidencias</strong> o <strong>Sugerencias</strong> para ver el kanban.</div>;
  }

  const columnas = FLUJOS[tipo]; // solo principales; laterales no se muestran ni son destino
  const activeTicket = activeId != null ? tickets.find((t) => String(t.id) === String(activeId)) : null;

  const idx = (estado) => columnas.indexOf(estado);
  // Espejo de puedeTransicionar restringido a principales (los laterales no son
  // columnas del kanban): avance LIBRE a cualquier estado posterior, retroceso ±1.
  // MANTENER SYNCED con backend services/flujosEstado.js
  const esValido = (origen, destino) => {
    const i = idx(origen), j = idx(destino);
    if (i === -1 || j === -1) return false;
    return j > i || (i - j === 1);
  };

  function aplicarCambio(ticket, destino, notaCambio) {
    const origen = ticket.estado;
    setTickets((prev) => prev.map((t) => (t.id === ticket.id ? { ...t, estado: destino } : t))); // optimista
    const payload = { estado: destino };
    if (notaCambio && notaCambio.trim()) payload.nota_cambio = notaCambio.trim();
    return patchPanelTicket(ticket.id, payload)
      .then(() => { toast.success(`Movido a ${ESTADO_META[destino]?.label || destino}`); })
      .catch((err) => {
        setTickets((prev) => prev.map((t) => (t.id === ticket.id ? { ...t, estado: origen } : t))); // rollback
        const code = err?.response?.data?.error;
        if (err?.response?.status === 400 && code === 'transicion_invalida') {
          toast.warn('El ticket ya estaba en otro estado. Recarga el panel.');
        } else {
          toast.error('No se pudo actualizar. Reintenta.');
        }
      });
  }

  function onDragStart(e) { draggedRef.current = true; setActiveId(e.active.id); }
  function limpiarDrag() { setActiveId(null); setTimeout(() => { draggedRef.current = false; }, 0); }
  function onDragEnd(e) {
    limpiarDrag();
    const { active, over } = e;
    if (!over) return;
    const ticket = tickets.find((t) => String(t.id) === String(active.id));
    if (!ticket) return;
    const destino = String(over.id);
    const origen = ticket.estado;
    if (destino === origen) return;
    if (!esValido(origen, destino)) return; // defensa (las inválidas ya van atenuadas)
    if (ESTADOS_EMAIL.includes(destino)) { setNota(''); setModal({ ticket, destino }); }
    else aplicarCambio(ticket, destino, null);
  }

  async function confirmarModal() {
    if (!modal) return;
    setGuardando(true);
    await aplicarCambio(modal.ticket, modal.destino, nota);
    setGuardando(false);
    setModal(null); setNota('');
  }
  function cerrarModal() { if (!guardando) { setModal(null); setNota(''); } }

  return (
    <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd} onDragCancel={limpiarDrag}>
      <div>
        {total > 100 && <div className="mb-3 text-xs text-amber-700">Mostrando los primeros 100 de {total} tickets.</div>}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-start">
          {columnas.map((col) => {
            const items = tickets.filter((t) => t.estado === col);
            const dim = !!activeTicket && col !== activeTicket.estado && !esValido(activeTicket.estado, col);
            return (
              <ColumnaDroppable key={col} col={col} dim={dim} count={items.length}>
                {items.map((t) => (
                  <CardArrastrable key={t.id} t={t} navigate={navigate} draggedRef={draggedRef} />
                ))}
              </ColumnaDroppable>
            );
          })}
        </div>
      </div>

      <DragOverlay>
        {activeTicket ? (
          <KanbanCard priority={activeTicket.prioridad} aging={agingDe(activeTicket.createdAt)}
            id={activeTicket.numero} title={activeTicket.titulo}
            meta={`${activeTicket.autor?.nombre || '—'} · ${hace(activeTicket.createdAt)}`} className="shadow-lg" />
        ) : null}
      </DragOverlay>

      <Dialog open={!!modal} onClose={cerrarModal} size="sm">
        <DialogHeader>
          <DialogTitle>Confirmar cambio de estado</DialogTitle>
        </DialogHeader>
        <DialogContent>
          {modal && (
            <>
              <p className="text-sm text-slate-600 mb-3">
                Mover <strong>{modal.ticket.numero}</strong> a <strong>{ESTADO_META[modal.destino]?.label || modal.destino}</strong>.
              </p>
              <Textarea rows={3} value={nota} onChange={(e) => setNota(e.target.value)}
                placeholder="Añade una nota interna sobre este cambio (opcional)" />
              <p className="text-xs text-slate-500 flex items-center gap-1.5 mt-2">
                <Mail className="w-3 h-3" /> Al guardar, se notificará por email al usuario.
              </p>
            </>
          )}
        </DialogContent>
        <DialogFooter>
          <Button variant="outline" onClick={cerrarModal} disabled={guardando}>Cancelar</Button>
          <Button onClick={confirmarModal} disabled={guardando}>
            {guardando ? <><Loader2 className="w-4 h-4 animate-spin" /> Guardando…</> : 'Guardar y notificar'}
          </Button>
        </DialogFooter>
      </Dialog>
    </DndContext>
  );
}
